import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Circle,
  ClipboardCheck,
  FileText,
  Play,
  Video,
  type LucideIcon,
} from "lucide-react";
import {
  myModuleView,
  type LessonTypeStr,
  type ModuleFinalQuizOut,
  type ModuleLessonViewOut,
  type StudentModuleViewOut,
} from "../api/assignments";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { MobileTabBar } from "../components/MobileTabBar";

type TabKey = "all" | "theory" | "practice" | "tests";

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "all", label: "Все уроки" },
  { key: "theory", label: "Теория" },
  { key: "practice", label: "Практика" },
  { key: "tests", label: "Тесты" },
];

const LESSON_TYPE_BADGE: Record<LessonTypeStr, string> = {
  text: "Теория",
  video: "Видео",
  file: "Материал",
  checklist: "Чек-лист",
  quiz: "Тест",
  mixed: "Урок",
};

const LESSON_TYPE_ICON: Record<LessonTypeStr, LucideIcon> = {
  text: FileText,
  video: Video,
  file: FileText,
  checklist: ClipboardCheck,
  quiz: ClipboardCheck,
  mixed: FileText,
};

function isTheory(t: LessonTypeStr): boolean {
  return t === "text" || t === "video" || t === "mixed";
}
function isPractice(t: LessonTypeStr): boolean {
  return t === "checklist" || t === "file";
}
function isTest(t: LessonTypeStr): boolean {
  return t === "quiz";
}

export function StudentModulePage() {
  const { moduleId } = useParams<{ moduleId: string }>();
  const id = Number(moduleId);
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [view, setView] = useState<StudentModuleViewOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("all");

  useEffect(() => {
    if (!Number.isFinite(id)) {
      setError("Некорректный модуль");
      return;
    }
    void myModuleView(id)
      .then(setView)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 403)
          setError("Нет доступа к модулю");
        else if (err instanceof ApiError && err.status === 404)
          setError("Модуль не найден");
        else
          setError(
            err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна",
          );
      });
  }, [id]);

  const counts = useMemo(() => {
    if (!view) return { all: 0, theory: 0, practice: 0, tests: 0 };
    let theory = 0,
      practice = 0,
      tests = 0;
    for (const l of view.lessons) {
      if (isTheory(l.lesson_type)) theory++;
      else if (isPractice(l.lesson_type)) practice++;
      else if (isTest(l.lesson_type)) tests++;
    }
    // Финальный тест модуля считаем в "Тесты" и в "Все" — он отображается
    // отдельной карточкой над списком уроков, не как ModuleLesson.
    const finalQuizExtra = view.final_quiz ? 1 : 0;
    return {
      all: view.lessons.length + finalQuizExtra,
      theory,
      practice,
      tests: tests + finalQuizExtra,
    };
  }, [view]);

  const filtered = useMemo(() => {
    if (!view) return [];
    if (tab === "all") return view.lessons;
    if (tab === "theory") return view.lessons.filter((l) => isTheory(l.lesson_type));
    if (tab === "practice")
      return view.lessons.filter((l) => isPractice(l.lesson_type));
    return view.lessons.filter((l) => isTest(l.lesson_type));
  }, [view, tab]);

  if (!user) return null;

  if (error) {
    return (
      <div className="min-h-full bg-surface pb-20 md:pb-8">
        <TopBar onBack={() => navigate(-1)} title="Модуль" />
        <div className="mx-auto max-w-3xl px-4 py-12 text-center text-ink-muted">
          <p>{error}</p>
          <Link
            to="/my-courses"
            className="mt-4 inline-block text-sm text-brand hover:underline"
          >
            ← к моим курсам
          </Link>
        </div>
        <MobileTabBar active="courses" />
      </div>
    );
  }

  if (!view) {
    return (
      <div className="min-h-full bg-surface pb-20 md:pb-8">
        <TopBar onBack={() => navigate(-1)} title="Модуль" />
        <main className="mx-auto max-w-3xl px-4 pt-4">
          <div className="h-24 animate-pulse rounded-xl bg-surface-alt" />
          <div className="mt-4 h-10 animate-pulse rounded-lg bg-surface-alt" />
          <div className="mt-3 space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded-xl bg-surface-alt" />
            ))}
          </div>
        </main>
        <MobileTabBar active="courses" />
      </div>
    );
  }

  const nextLesson =
    view.lessons.find((l) => l.status !== "completed") ?? null;

  return (
    <div className="min-h-full bg-surface pb-32 md:pb-8">
      <TopBar
        onBack={() => navigate(`/learn/courses/${view.course_id}`)}
        title={`Модуль ${view.module_position + 1}`}
      />

      <main className="mx-auto max-w-3xl px-4 pt-4">
        <div className="mb-4">
          <h2 className="text-xl font-bold leading-snug text-ink">
            {view.module_title}
          </h2>
          {view.module_description && (
            <p className="mt-1 text-xs text-ink-muted">{view.module_description}</p>
          )}
        </div>

        {/* Module progress */}
        <section className="mb-5">
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="font-medium uppercase tracking-wide text-ink-muted">
              Прогресс модуля
            </span>
            <span className="font-semibold tabular-nums text-ink">
              {view.percent}%
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-alt">
            <div
              className={`h-full rounded-full transition-all ${
                view.percent === 100 ? "bg-brand-success" : "bg-brand"
              }`}
              style={{ width: `${view.percent}%` }}
            />
          </div>
          <div className="mt-1 text-[11px] text-ink-muted">
            {view.lessons_completed} из {view.lessons_total} уроков
          </div>
        </section>

        {/* Tabs */}
        <div className="-mx-1 mb-3 flex gap-1 overflow-x-auto pb-1">
          {TABS.map((t) => {
            const n =
              t.key === "all"
                ? counts.all
                : t.key === "theory"
                  ? counts.theory
                  : t.key === "practice"
                    ? counts.practice
                    : counts.tests;
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-sm transition ${
                  active
                    ? "bg-brand text-white"
                    : "bg-surface-alt text-ink-soft hover:bg-line"
                }`}
              >
                {t.label}{" "}
                <span
                  className={`ml-0.5 tabular-nums ${
                    active ? "text-white/80" : "text-ink-muted"
                  }`}
                >
                  {n}
                </span>
              </button>
            );
          })}
        </div>

        {/* Final quiz card — отдельная карточка над списком уроков.
            Показывается во вкладках Все/Тесты, скрывается в Теория/Практика. */}
        {view.final_quiz && (tab === "all" || tab === "tests") && (
          <div className="mb-2">
            <FinalQuizCard
              quiz={view.final_quiz}
              lessonsCompleted={view.lessons_completed === view.lessons_total && view.lessons_total > 0}
            />
          </div>
        )}

        {/* Lessons */}
        {filtered.length === 0 && !(view.final_quiz && (tab === "all" || tab === "tests")) ? (
          <div className="card mt-2 p-6 text-center text-sm text-ink-muted">
            В этой вкладке уроков нет
          </div>
        ) : (
          <ol className="space-y-2">
            {filtered.map((l) => (
              <li key={l.lesson_id}>
                <LessonRow
                  lesson={l}
                  moduleId={view.module_id}
                  moduleNumber={view.module_position + 1}
                  isNext={nextLesson?.lesson_id === l.lesson_id}
                />
              </li>
            ))}
          </ol>
        )}
      </main>

      {/* Bottom CTA */}
      {nextLesson && (
        <div className="fixed inset-x-0 bottom-14 z-10 border-t border-line bg-white/95 px-4 py-3 backdrop-blur md:bottom-0">
          <div className="mx-auto max-w-3xl">
            <Link
              to={`/learn/lessons/${nextLesson.lesson_id}?module=${view.module_id}`}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-dark"
            >
              {view.lessons_completed === 0
                ? "Начать обучение"
                : `Продолжить урок ${view.module_position + 1}.${nextLesson.position + 1}`}
              <ChevronRight size={16} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      )}

      <MobileTabBar active="courses" />
    </div>
  );
}

function TopBar({ onBack, title }: { onBack: () => void; title: string }) {
  return (
    <header className="sticky top-0 z-10 border-b border-line bg-surface/95 backdrop-blur">
      <div className="mx-auto flex max-w-3xl items-center px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          className="grid h-9 w-9 place-items-center rounded-lg text-ink-soft transition hover:bg-surface-alt"
          aria-label="Назад"
        >
          <ChevronLeft size={20} strokeWidth={2} />
        </button>
        <h1 className="flex-1 text-center text-base font-semibold text-ink">
          {title}
        </h1>
        <span className="h-9 w-9" aria-hidden />
      </div>
    </header>
  );
}

function LessonRow({
  lesson,
  moduleId,
  moduleNumber,
  isNext,
}: {
  lesson: ModuleLessonViewOut;
  moduleId: number;
  moduleNumber: number;
  isNext: boolean;
}) {
  const done = lesson.status === "completed";
  const lessonNum = `${moduleNumber}.${lesson.position + 1}`;
  const badge = LESSON_TYPE_BADGE[lesson.lesson_type] ?? "Урок";
  const Icon = LESSON_TYPE_ICON[lesson.lesson_type] ?? FileText;

  return (
    <Link
      to={`/learn/lessons/${lesson.lesson_id}?module=${moduleId}`}
      className={`group relative block rounded-xl bg-white ring-1 transition ${
        isNext
          ? "ring-brand/40 shadow-sm"
          : "ring-line hover:shadow-sm"
      }`}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <div
          className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-semibold ${
            done
              ? "bg-brand-success text-white"
              : isNext
                ? "bg-brand text-white"
                : "bg-surface-alt text-ink-soft"
          }`}
        >
          {done ? (
            <Check size={14} strokeWidth={2.5} />
          ) : isNext ? (
            <Play size={12} strokeWidth={2.5} fill="currentColor" />
          ) : (
            <Circle size={6} strokeWidth={0} fill="currentColor" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-medium text-ink-muted">
            Урок {lessonNum}
          </div>
          <div className="truncate text-sm font-medium text-ink">
            {lesson.title}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-1.5 py-0.5 font-medium text-brand">
              <Icon size={10} strokeWidth={2.25} />
              {badge}
            </span>
            {lesson.estimated_minutes && (
              <span className="text-ink-muted">{lesson.estimated_minutes} мин</span>
            )}
          </div>
        </div>
        <ChevronRight
          size={16}
          strokeWidth={2}
          className="shrink-0 text-ink-muted"
        />
      </div>
    </Link>
  );
}

function FinalQuizCard({
  quiz,
  lessonsCompleted,
}: {
  quiz: ModuleFinalQuizOut;
  lessonsCompleted: boolean;
}) {
  const isPassed = quiz.passed;
  const hasAttempts = quiz.attempts_used > 0;
  const exhausted =
    quiz.max_attempts !== null && quiz.attempts_used >= quiz.max_attempts && !isPassed;

  const ctaLabel = isPassed
    ? "Пройти ещё раз"
    : hasAttempts
      ? "Продолжить тест"
      : "Начать тест";

  // Состояние карточки определяет цвет рамки и иконки:
  // - сдан → зелёная success
  // - все попытки сожжены без сдачи → красная danger
  // - в работе/не начат → нейтральная brand
  const tone: "success" | "danger" | "brand" = isPassed
    ? "success"
    : exhausted
      ? "danger"
      : "brand";

  const toneClasses: Record<typeof tone, string> = {
    success: "border-brand-success/40 bg-brand-success/5",
    danger: "border-brand-danger/40 bg-brand-danger/5",
    brand: "border-brand/30 bg-brand/5",
  };

  return (
    <Link
      to={`/learn/quizzes/${quiz.quiz_id}`}
      className={`flex items-center gap-3 rounded-xl border-2 p-4 transition hover:shadow-sm ${toneClasses[tone]}`}
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white ${
          tone === "success"
            ? "bg-brand-success"
            : tone === "danger"
              ? "bg-brand-danger"
              : "bg-brand"
        }`}
      >
        {isPassed ? (
          <Check size={20} strokeWidth={3} />
        ) : (
          <ClipboardCheck size={20} strokeWidth={2} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
            Финальный тест модуля
          </span>
          {isPassed && (
            <span className="badge badge-success text-[10px]">
              Сдан · {quiz.best_score_percent}%
            </span>
          )}
        </div>
        <div className="mt-0.5 truncate text-sm font-medium text-ink">
          {quiz.title}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-ink-muted">
          <span>Проходной балл {quiz.passing_score_percent}%</span>
          {quiz.max_attempts !== null ? (
            <>
              <span>·</span>
              <span>
                Попыток: {quiz.attempts_used}/{quiz.max_attempts}
              </span>
            </>
          ) : quiz.attempts_used > 0 ? (
            <>
              <span>·</span>
              <span>Попыток сделано: {quiz.attempts_used}</span>
            </>
          ) : null}
          {!isPassed &&
            quiz.best_score_percent !== null && (
              <>
                <span>·</span>
                <span>Лучший результат: {quiz.best_score_percent}%</span>
              </>
            )}
          {!lessonsCompleted && !isPassed && (
            <>
              <span>·</span>
              <span className="text-brand-danger">Пройдите уроки модуля</span>
            </>
          )}
        </div>
        {exhausted && (
          <div className="mt-1 text-[11px] text-brand-danger">
            Лимит попыток исчерпан — попросите куратора выдать ещё одну
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1 text-sm font-medium text-brand">
        <span className="hidden sm:inline">{ctaLabel}</span>
        <Play size={14} strokeWidth={2.5} />
      </div>
    </Link>
  );
}
